import {noop} from 'es-toolkit'
import {getIn, useFormik} from 'formik'
import * as Yup from 'yup'

import config from '../../config'

import {formatPercentage, formatPrice} from '../../libs/formatter'
import {
  calculateModelGeometryPrice,
  calculateModelMachiningPrice,
  calculatePrices,
  getStaggeredVolumePriceItem,
  getStaggeredWeightPriceItem,
  getTotalWeightInKg,
} from '../../libs/pricing'
import * as priceApi from '../../services/api/price'

import {PriceFormula, Provider} from '../../types'

import FieldLabel from '../../../../commons/src/components/field-label/FieldLabel'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Accordion from '../../components/accordion'
import AccordionItem from '../../components/accordion/AccordionItem'
import ContentBox from '../../components/content-box'
import LabeledPrice from '../../components/labeled-price'

import {PricingType, VolumePricingType, WeightPricingType} from '../forms/pricings/form'

type Props = {
  provider: Provider
  priceRequest?: Partial<priceApi.PriceRequest>
  pricingType?: PricingType
  volumePricingType?: VolumePricingType
  weightPricingType?: WeightPricingType
}

type FormData = {
  volume: string
  boundingBox: string
  surfaceArea: string
  quantity: string
  setupCount: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const validationSchema = Yup.object().shape({
  volume: Yup.number().min(0).required(),
  boundingBox: Yup.number().min(0),
  surfaceArea: Yup.number().min(0),
  quantity: Yup.number().min(1).required(),
  setupCount: Yup.number().integer().min(1),
})

const initialValues: FormData = {
  volume: '',
  boundingBox: '',
  surfaceArea: '',
  quantity: '1',
  setupCount: '1',
}

const getModelFee = (provider: Provider) => {
  const providerCurrency = provider.currency as keyof typeof config.priceCalculation.modelFee
  return config.priceCalculation.modelFee[providerCurrency] || config.priceCalculation.modelFee.USD
}

const PriceCalculatorPartial = ({
  priceRequest,
  provider,
  pricingType,
  volumePricingType,
  weightPricingType,
}: Props) => {
  const formik = useFormik<FormData>({
    initialValues,
    initialErrors: {formikGeneral: 'initial error'},
    validationSchema,
    onSubmit: noop,
  })

  const modelProperties = {
    volume: formik.values.volume ? parseFloat(formik.values.volume) : undefined,
    boundingBox: formik.values.boundingBox ? parseFloat(formik.values.boundingBox) : undefined,
    surfaceArea: formik.values.surfaceArea ? parseFloat(formik.values.surfaceArea) : undefined,
    quantity: formik.values.quantity ? parseFloat(formik.values.quantity) : 1,
    setupCount: formik.values.setupCount ? parseInt(formik.values.setupCount, 10) : undefined,
  }

  let calculatedPrices: (ReturnType<typeof calculatePrices> & {modelPrice: number}) | null = null
  if (formik.isValid && priceRequest) {
    if (
      Object.values(modelProperties).every(Number) ||
      (priceRequest.formula === PriceFormula.THREE_D_PRINTING && modelProperties.volume)
    ) {
      const modelPrice =
        priceRequest.formula === PriceFormula.CNC_MACHINING
          ? calculateModelMachiningPrice(priceRequest, modelProperties)
          : calculateModelGeometryPrice(priceRequest, modelProperties)

      calculatedPrices = {
        modelPrice,
        ...calculatePrices(
          priceRequest,
          modelPrice,
          modelProperties.quantity,
          config.priceCalculation.pricePremiumShare,
          getModelFee(provider),
          config.priceCalculation.craftcloudMargin,
          config.priceCalculation.profitShare
        ),
      }
    }
  }

  const hasError = (name: keyof FormData) =>
    formik.values[name] !== initialValues[name] && !!getIn(formik.errors, name)

  const parameterValues = [
    formik.values.volume,
    formik.values.boundingBox,
    formik.values.surfaceArea,
    formik.values.quantity,
    formik.values.setupCount,
  ]
  const showAllParametersRequiredNote =
    priceRequest?.formula === PriceFormula.CNC_MACHINING &&
    parameterValues.some(value => value !== '') &&
    !parameterValues.every(value => value !== '')

  const showBoundingBoxParameterMissingNote =
    priceRequest?.formula === PriceFormula.THREE_D_PRINTING &&
    [formik.values.volume, formik.values.surfaceArea].some(value => value !== '') &&
    formik.values.boundingBox === '' &&
    !!priceRequest?.boundingBoxPrice

  const showSurfaceAreaParameterMissingNote =
    priceRequest?.formula === PriceFormula.THREE_D_PRINTING &&
    [formik.values.volume, formik.values.boundingBox].some(value => value !== '') &&
    formik.values.surfaceArea === '' &&
    !!priceRequest?.surfacePrice

  return (
    <>
      <ContentBox>
        <Paragraph size="s" minor>
          You can use our example pricing calculator to find out how your price structure will be
          reflected on Craftcloud.
        </Paragraph>
        <Headline size="s" label="Example model parameters" className="u-margin-bottom-xs" />
        <FormRow layout="half-half">
          <FieldLabel label="Volume">
            <NumberInputField
              allowDecimals
              name="volume"
              value={formik.values.volume}
              suffix="cm³"
              appearance={hasError('volume') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldLabel>
          <FieldLabel label="Bounding box volume">
            <NumberInputField
              allowDecimals
              name="boundingBox"
              value={formik.values.boundingBox}
              suffix="cm³"
              appearance={hasError('boundingBox') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldLabel>
        </FormRow>
        <FormRow layout="half-half">
          <FieldLabel label="Surface area">
            <NumberInputField
              allowDecimals
              name="surfaceArea"
              value={formik.values.surfaceArea}
              suffix="cm²"
              appearance={hasError('surfaceArea') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldLabel>
          <FieldLabel label="Quantity">
            <NumberInputField
              allowDecimals
              name="quantity"
              suffix="pieces"
              value={formik.values.quantity}
              appearance={hasError('quantity') ? 'error' : undefined}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldLabel>
        </FormRow>
        {priceRequest?.formula === PriceFormula.CNC_MACHINING && (
          <FormRow layout="half-half">
            <FieldLabel label="Setup Count">
              <NumberInputField
                name="setupCount"
                suffix="setups"
                value={formik.values.setupCount}
                appearance={hasError('setupCount') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldLabel>
          </FormRow>
        )}
        {showAllParametersRequiredNote && (
          <Paragraph size="s" warning>
            All model parameters are required to calculate the price.
          </Paragraph>
        )}
        {showBoundingBoxParameterMissingNote && (
          <Paragraph size="s" warning>
            <em>Bounding box volume</em> is required to calculate the price correctly.
          </Paragraph>
        )}
        {showSurfaceAreaParameterMissingNote && (
          <Paragraph size="s" warning>
            <em>Surface area</em> is required to calculate the price correctly.
          </Paragraph>
        )}
        {pricingType === PricingType.VOLUME &&
          volumePricingType === VolumePricingType.STAGGERED && (
            <LabeledPrice label="Selected volume price">
              {priceRequest && modelProperties.volume
                ? formatPrice(
                    getStaggeredVolumePriceItem(priceRequest, modelProperties.volume),
                    provider.currency,
                    2
                  )
                : '-'}
            </LabeledPrice>
          )}
        {pricingType === PricingType.WEIGHT &&
          weightPricingType === WeightPricingType.STAGGERED && (
            <LabeledPrice label="Selected weight price">
              {priceRequest && formik.values.volume
                ? formatPrice(
                    getStaggeredWeightPriceItem(
                      priceRequest,
                      getTotalWeightInKg(priceRequest, modelProperties)
                    ),
                    provider.currency,
                    2
                  )
                : '-'}
            </LabeledPrice>
          )}
        <LabeledPrice
          label={
            priceRequest?.formula === PriceFormula.CNC_MACHINING
              ? 'Model machining price'
              : 'Model geometry price'
          }
        >
          {calculatedPrices ? formatPrice(calculatedPrices.modelPrice, provider.currency, 2) : '-'}
        </LabeledPrice>
        <LabeledPrice label="Price per piece ">
          {calculatedPrices ? formatPrice(calculatedPrices.piecePrice, provider.currency, 2) : '-'}
        </LabeledPrice>
        <LabeledPrice label="Quantity discounted price per piece">
          {calculatedPrices
            ? formatPrice(calculatedPrices.quantityDiscountedPiecePrice, provider.currency, 2)
            : '-'}
        </LabeledPrice>
        <LabeledPrice label="Your price per file" strong>
          {calculatedPrices
            ? formatPrice(calculatedPrices.totalPricePerFile, provider.currency, 2)
            : '-'}
        </LabeledPrice>
        <LabeledPrice label="Price per file (incl. Craftcloud costs)" className="u-margin-top-l">
          {calculatedPrices
            ? formatPrice(calculatedPrices.totalPriceWithCCMarginAndModelFee, provider.currency, 2)
            : '-'}
        </LabeledPrice>
        <LabeledPrice label="Additional margin">
          {calculatedPrices
            ? formatPrice(calculatedPrices.totalProfitShare, provider.currency, 2)
            : '-'}
        </LabeledPrice>
        <LabeledPrice label="Final customer price">
          {calculatedPrices
            ? formatPrice(calculatedPrices.totalCustomerPriceV2, provider.currency, 2)
            : '-'}
        </LabeledPrice>
        <LabeledPrice label="Your earnings" strong>
          {calculatedPrices
            ? formatPrice(calculatedPrices.invoiceableAmountV2, provider.currency, 2)
            : '-'}
        </LabeledPrice>
      </ContentBox>
      <Accordion className="u-margin-top-xs">
        <AccordionItem title="Price calculation details" isOpen>
          {priceRequest?.formula === PriceFormula.CNC_MACHINING && (
            <>
              {pricingType === PricingType.WEIGHT &&
                weightPricingType === WeightPricingType.STAGGERED && (
                  <Paragraph size="s">
                    <strong>Selected weight price</strong>
                    <br />
                    The price is determined by the nearest lower threshold to the total{' '}
                    <em>bounding box weight</em> of all pieces (model bounding box volume * density
                    * quantity)
                  </Paragraph>
                )}
              <Paragraph size="s">
                <strong>Material cost</strong>
                <br />
                Bounding box volume * density *{' '}
                {weightPricingType === WeightPricingType.STAGGERED
                  ? 'selected weight price'
                  : 'weight price'}
              </Paragraph>
              <Paragraph size="s">
                <strong>Rough machining cost</strong>
                <br />
                (Bounding box volume - volume) * material removal rate * machining cost per hour
              </Paragraph>
              <Paragraph size="s">
                <strong>Finishing cost</strong>
                <br />
                Surface area * finishing removal rate * machining cost per hour
              </Paragraph>
              <Paragraph size="s">
                <strong>Refixture time cost</strong>
                <br />
                (Setup count - 1) * machine refixture time in hours * machining cost per hour
              </Paragraph>
              <Paragraph size="s">
                <strong>CAM programming fee</strong>
                <br />
                Setup count * CAM programming fee per setup
              </Paragraph>
              <Paragraph size="s">
                <strong>Model machining price</strong>
                <br />
                Material cost + rough machining cost + finishing cost + refixture time cost + CAM
                programming fee
              </Paragraph>
              <Paragraph size="s">
                <strong>Price per piece</strong>
                <br />
                Maximum of (Base price per piece + model machining price, minimum price per piece)
              </Paragraph>
            </>
          )}
          {priceRequest?.formula !== PriceFormula.CNC_MACHINING && (
            <>
              {pricingType === PricingType.VOLUME &&
                volumePricingType === VolumePricingType.STAGGERED && (
                  <Paragraph size="s">
                    <strong>Selected volume price</strong>
                    <br />
                    The price is determined by the nearest lower threshold to the model’s volume.
                  </Paragraph>
                )}
              {pricingType === PricingType.WEIGHT &&
                weightPricingType === WeightPricingType.STAGGERED && (
                  <Paragraph size="s">
                    <strong>Selected weight price</strong>
                    <br />
                    The price is determined by the nearest lower threshold to the total weight of
                    all pieces (model weight * quantity)
                  </Paragraph>
                )}
              <Paragraph size="s">
                <strong>Model geometry price</strong>
                <br />
                {!pricingType ||
                  (pricingType === PricingType.VOLUME && (
                    <>
                      (
                      {volumePricingType === VolumePricingType.STAGGERED
                        ? 'Selected volume price'
                        : 'Volume price'}{' '}
                      * volume) + (bounding box price * bounding box volume) + (surface price *
                      surface area)
                    </>
                  ))}
                {pricingType === PricingType.WEIGHT && (
                  <>
                    (
                    {weightPricingType === WeightPricingType.STAGGERED
                      ? 'Selected weight price'
                      : 'Weight price'}{' '}
                    * volume * density) + (bounding box price * bounding box) + (surface price *
                    surface area)
                  </>
                )}
              </Paragraph>
              <Paragraph size="s">
                <strong>Price per piece</strong>
                <br />
                Maximum of (Base price per piece + model geometry price, minimum price per piece)
              </Paragraph>
            </>
          )}
          <Paragraph size="s">
            <strong>Quantity discounted price per piece</strong>
            <br />
            Maximum of (Quantity ^ quantity discount, quantity discount cap) * price per piece
          </Paragraph>
          <Paragraph size="s">
            <strong>Your price per file</strong>
            <br />
            Maximum of (base price per file + quantity discounted price per piece * quantity,
            minimum file price)
          </Paragraph>
          <Paragraph size="s">
            <strong>Price per file (incl. Craftcloud costs)</strong>
            <br />
            {`Your price per file + Craftcloud base fee (${formatPercentage(config.priceCalculation.craftcloudMargin)}) + Craftcloud model handling fee (${formatPrice(getModelFee(provider), provider.currency)} per file${provider.currency !== 'USD' ? '*' : ''})`}
          </Paragraph>
          <Paragraph size="s">
            <strong>Additional share margin</strong>
            <br />
            {`${formatPercentage(config.priceCalculation.profitShare)} of total price`}
          </Paragraph>
          <Paragraph size="s">
            <strong>Final customer price</strong>
            <br />
            Total price + total share margin
          </Paragraph>
          <Paragraph size="s">
            <strong>Your earnings</strong>
            <br />
            Total price + (50% of total share margin)
          </Paragraph>
          {provider.currency !== 'USD' && (
            <Paragraph size="s" minor>
              * Currency exchange rates may affect the Craftcloud model handling fee
            </Paragraph>
          )}
        </AccordionItem>
      </Accordion>
    </>
  )
}

export default PriceCalculatorPartial
